/// <reference path="globals.d.ts" />
/// <reference path="prompts.ts" />
/// <reference path="pipeline-core.ts" />
/// <reference path="pipeline-engines.ts" />
// pipeline-campaign.ts — Studio: one look per Generate, in the chosen brand,
// season and format, always at 1K, from the prompt manager's STUDIO_GENERATE.
// Klassisk script — deler global scope.

// The engines' own 1K canvas for each format (Gemini 3 Pro Image, which KIE's
// nano-banana-pro also is). A look is delivered at exactly this size; an engine
// that answers in another shape is cropped to it, never padded.
const STUDIO_FORMAT_SIZES: Record<string, { w: number; h: number }> = {
    '1:1':  { w: 1024, h: 1024 },
    '4:5':  { w: 928,  h: 1152 },
    '9:16': { w: 768,  h: 1376 },
};

// The pictures a webshop shows of a garment worn (as redgreen.dk does): half
// body, full body and a close-up. The full body is the edit of the reference
// photo ({{SHOT}} in STUDIO_GENERATE is its framing); half body and close-up are
// made from it (generateStudioShot), as `crop` says.
const STUDIO_SHOTS: { id: string; label: string; name: string; frame: string; crop?: string }[] = [
    { id: 'half', label: 'Half', name: 'Half body', crop: 'half body, from just above the head to mid-thigh',
      frame: 'Half body, as a webshop shows a garment worn: the person from just above the head to mid-thigh, upright in the middle of the frame; for trousers, a skirt or shoes, from the waist down to the feet. If the photograph shows more or less of them, the camera has stepped closer or further back, at the same height.' },
    { id: 'full', label: 'Full', name: 'Full body',
      frame: 'Full body, as a webshop shows a garment worn: the whole person from just above the head to just below the shoes, upright in the middle of the frame. Where the photograph cuts them off, the camera has stepped back: the rest of them, their other clothes and their shoes are as the notes describe, or as they would be.' },
    { id: 'close', label: 'Close-up', name: 'Close-up', crop: 'a close-up of the garment on the person, its details filling the frame',
      frame: 'Close-up, as a webshop shows a garment\'s details: the garment on the person, so close that the part with its most telling details fills the frame: the collar, the buttons, the print or logo and the fabric of a top, from the chin to the middle of the chest, the face cut off at the mouth; the waist, pockets and fabric of trousers. The camera has come close: the weave and the stitching are sharp.' },
];
// A look from before shots keeps the photograph's own framing.
const STUDIO_SHOT_AS_PHOTO = 'The framing of the photograph: keep the person whole and at the same scale where the frame allows, and crop where there is room.';

// Studio makes a look in two steps, each from its own prompt in the prompt
// manager (/prompts):
//   1. STUDIO_ANALYZE: a text model reads the chosen reference photos and the
//      packshot, and writes the notes for the edit: which piece of clothing
//      the packshot replaces, how it is worn, the person, the rest of the
//      outfit, the pose and the photo. Fields: {{BRAND}} {{SEASON}}
//      {{REFERENCE_COUNT}} {{GARMENT_COUNT}} {{GARMENT_VIEWS}}.
//   2. STUDIO_GENERATE: the image model edits the first chosen reference photo
//      (sent first), with the packshot after it: the same person, the other
//      clothes, the pose and the place, with the packshot garment put on.
//      Fields:
//        {{BRAND}}            the brand's name, e.g. North Latitude
//        {{SEASON}}           Summer or Winter
//        {{FORMAT}}           1:1, 4:5 or 9:16
//        {{WIDTH}} {{HEIGHT}} the look's size in pixels
//        {{GARMENT_COUNT}}    how many packshot images there are, 1 or 2
//        {{GARMENT_VIEWS}}    which views they are: front, back
//        {{REFERENCE_COUNT}}  how many reference photos were read, 1 to 6
//        {{REFERENCE_DESCRIPTION}}  the notes step 1 wrote
//        {{SHOT}}             the framing: half body, full body or close-up (STUDIO_SHOTS)
// Each prompt is the whole text: Studio adds nothing of its own. Without both
// prompts, or without a reference photo, Studio does not generate.
function studioPromptTemplate(): string {
    return String(PROMPTS.STUDIO_GENERATE || '').trim();
}
function studioAnalysisTemplate(): string {
    return String(PROMPTS.STUDIO_ANALYZE || '').trim();
}

function fillStudioFields(tpl: string, fields: Record<string, string>): string {
    return tpl.replace(/\{\{([A-Z_]+)\}\}/g, (all, key: string) => (key in fields ? fields[key] : all));
}

function buildStudioAnalysisPrompt(req: StudioRequest): string {
    const tpl = studioAnalysisTemplate();
    if (!tpl) return '';
    return fillStudioFields(tpl, {
        BRAND: req.brand.name,
        SEASON: req.seasonLabel,
        REFERENCE_COUNT: String(req.referenceFiles.length),
        GARMENT_COUNT: String(req.garmentFiles.length),
        GARMENT_VIEWS: req.garmentViews.join(', ') || 'front',
    });
}

function buildStudioPrompt(req: StudioRequest, description: string): string {
    const tpl = studioPromptTemplate();
    if (!tpl) return '';
    const size = STUDIO_FORMAT_SIZES[req.aspect] || STUDIO_FORMAT_SIZES['1:1'];
    return fillStudioFields(tpl, {
        BRAND: req.brand.name,
        SEASON: req.seasonLabel,
        FORMAT: req.aspect,
        WIDTH: String(size.w),
        HEIGHT: String(size.h),
        GARMENT_COUNT: String(req.garmentFiles.length),
        GARMENT_VIEWS: req.garmentViews.join(', ') || 'front',
        REFERENCE_COUNT: String(req.referenceFiles.length),
        REFERENCE_DESCRIPTION: description,
        SHOT: (STUDIO_SHOTS.find(s => s.id === req.shot) || { frame: STUDIO_SHOT_AS_PHOTO }).frame,
    });
}

// The references are read by Gemini 3.1 Pro, at its own temperature (Google
// asks for 1 on Gemini 3); 2.5 Pro takes over when 3.1 is overloaded.
const STUDIO_ANALYSIS_MODELS = ['gemini-3.1-pro-preview', 'gemini-3.8-flash'];

// One set of notes per choice of reference photos, packshot and analysis
// prompt, so "Generate again" costs no new read and keeps the same notes. The
// shots of one Generate start together and share the one read on its way.
const __studioDescriptions = new Map<string, Promise<string>>();

async function describeStudioReferences(req: StudioRequest, onStage: (msg: string) => void): Promise<string> {
    const prompt = buildStudioAnalysisPrompt(req);
    const key = req.referenceKey + '|' + req.garmentKey + '|' + prompt.length + ':' + prompt.slice(-60);
    let notes = __studioDescriptions.get(key);
    if (!notes) {
        notes = (async () => {
            const text = String(await geminiText(prompt, [...req.referenceFiles, ...req.garmentFiles], { models: STUDIO_ANALYSIS_MODELS, temperature: 1 }) || '').trim();
            if (!text) throw new Error('The reference photos could not be read');
            return text;
        })();
        __studioDescriptions.set(key, notes);
        notes.catch(() => __studioDescriptions.delete(key));
    }
    onStage('Reading the references');
    return notes;
}

// Centre-crops the engine's image to the format's shape and scales it to the
// format's 1K size. Gemini and KIE already answer at that size, so for them this
// only re-encodes; the OpenAI fallback answers 2:3 and is cropped here.
function studioFit(raw: ImageResult, aspect: string): Promise<ImageResult> {
    const size = STUDIO_FORMAT_SIZES[aspect] || STUDIO_FORMAT_SIZES['1:1'];
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            if (img.naturalWidth === size.w && img.naturalHeight === size.h) { resolve(raw); return; }
            const target = size.w / size.h;
            let sw = img.naturalWidth, sh = img.naturalHeight;
            if (sw / sh > target) sw = Math.round(sh * target); else sh = Math.round(sw / target);
            const sx = Math.round((img.naturalWidth - sw) / 2);
            const sy = Math.round((img.naturalHeight - sh) / 2);
            const canvas = document.createElement('canvas');
            canvas.width = size.w;
            canvas.height = size.h;
            const ctx = canvas.getContext('2d');
            if (!ctx) { resolve(raw); return; }
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, sx, sy, sw, sh, 0, 0, size.w, size.h);
            const url = canvas.toDataURL('image/png');
            resolve({ data: url.slice(url.indexOf(',') + 1), mime: 'image/png', engine: raw.engine, model: raw.model });
        };
        img.onerror = () => resolve(raw);
        img.src = `data:${raw.mime || 'image/png'};base64,${raw.data}`;
    });
}

// ── Half body and close-up ──
// Asked of the edit itself, the image model keeps the photograph's framing: on
// 2026-10-07 the half body came out as the full body. So the closer shots are made
// from the finished full body look: a text model finds the shot's box in it, the
// box is cut out in the format's shape, and the image model makes that crop the
// sharp photograph it would have been (STUDIO_SHOT), with the packshot for the
// garment. Image models keep the framing they are given, so this one holds.
const STUDIO_SHOT_BOX: Record<string, string> = {
    half: 'a half-body picture of the person, as a webshop shows a garment worn: from just above the top of the head to mid-thigh (for a person sitting, to the knees), with the person in the middle from left to right',
    close: 'a close-up of the garment on the person, as a webshop shows its details: the part with its most telling details (the collar, the chest with its print or logo, the buttons) filling the box; for a top, from the chin to the middle of the chest; for trousers, the waist and the pockets. Of the face, at most the chin',
};
// Where the box is when the answer cannot be read: [top, left, bottom, right], 0-1.
const STUDIO_SHOT_BOX_FALLBACK: Record<string, number[]> = { half: [0, 0.15, 0.62, 0.85], close: [0.14, 0.22, 0.46, 0.78] };
// Gemini 3.1 Pro finds the box in a few seconds; 3.8 Flash takes over when it is
// busy. (2.5 Flash was retired by Google: on 2026-10-07 it answered 404.)
const STUDIO_BOX_MODELS = ['gemini-3.1-pro-preview', 'gemini-3.8-flash'];

function studioShotBoxPrompt(shot: string): string {
    return 'This is a fashion photograph. Find the crop box for ' + STUDIO_SHOT_BOX[shot] + '.\n'
        + 'Answer with JSON only: {"box_2d": [ymin, xmin, ymax, xmax]}, the coordinates from 0 to 1000.';
}

async function studioShotBox(base: File, shot: string): Promise<number[]> {
    try {
        const text = await geminiText(studioShotBoxPrompt(shot), [base], { models: STUDIO_BOX_MODELS, temperature: 0 });
        const m = String(text).match(/\[\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*\]/);
        if (m) {
            const [y0, x0, y1, x1] = m.slice(1, 5).map(Number).map(v => Math.min(1000, Math.max(0, v)) / 1000);
            if (y1 - y0 > 0.05 && x1 - x0 > 0.05) return [y0, x0, y1, x1];
        }
        appendLog('  Shot box: no box in the answer; the usual one is used');
    } catch (e) {
        appendLog('  Shot box: ' + ((e as Error).message || e) + '; the usual one is used');
    }
    return STUDIO_SHOT_BOX_FALLBACK[shot];
}

// Cuts the box out of the picture in the format's shape, at the format's 1K size.
// Half body: all of the box, widened about its middle or lengthened downwards (its
// top is the head). Close-up: its top at the chin and its sides cut, rather than
// the picture made taller, so the garment stays large (tried on 2026-10-07). Made
// smaller if it does not fit, and kept inside the picture.
function studioCropToFormat(file: File, box: number[], aspect: string, shot: string = 'half'): Promise<File> {
    const size = STUDIO_FORMAT_SIZES[aspect] || STUDIO_FORMAT_SIZES['1:1'];
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            const W = img.naturalWidth, H = img.naturalHeight, target = size.w / size.h;
            const [y0, x0, y1, x1] = [box[0] * H, box[1] * W, box[2] * H, box[3] * W];
            const bw = x1 - x0, h0 = y1 - y0;
            let w = bw, h = h0;
            if (shot === 'close') { w = Math.max(h0 * target, 0.7 * bw); h = w / target; }
            else if (w / h < target) w = h * target;
            else h = w / target;
            const fit = Math.min(1, W / w, H / h);
            w *= fit; h *= fit;
            const sx = Math.min(Math.max(0, (x0 + x1) / 2 - w / 2), W - w);
            const top = shot === 'close' ? y0 - 0.02 * H : y0 - Math.max(0, h - h0) * 0.15;
            const sy = Math.min(Math.max(0, top), H - h);
            const canvas = document.createElement('canvas');
            canvas.width = size.w;
            canvas.height = size.h;
            const ctx = canvas.getContext('2d');
            if (!ctx) { reject(new Error('No canvas')); return; }
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, sx, sy, w, h, 0, 0, size.w, size.h);
            canvas.toBlob((b) => b ? resolve(new File([b], 'shot-crop.jpg', { type: 'image/jpeg' })) : reject(new Error('The crop could not be made')), 'image/jpeg', 0.95);
        };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('The full body look could not be read')); };
        img.src = url;
    });
}

async function generateStudioShot(req: StudioRequest, onStage: (msg: string) => void): Promise<StudioLookResult> {
    const tpl = String((PROMPTS as any).STUDIO_SHOT || '').trim();
    if (!tpl) throw new Error('Studio has no prompt for this shot yet');
    if (!req.baseFile) throw new Error('The full body look is needed first');
    const shot = STUDIO_SHOTS.find(s => s.id === req.shot)!;
    onStage('Framing');
    await discoverModels();
    const box = await studioShotBox(req.baseFile, shot.id);
    const crop = await studioCropToFormat(req.baseFile, box, req.aspect, shot.id);
    const size = STUDIO_FORMAT_SIZES[req.aspect] || STUDIO_FORMAT_SIZES['1:1'];
    const prompt = fillStudioFields(tpl, {
        BRAND: req.brand.name,
        SEASON: req.seasonLabel,
        SHOT: shot.crop || shot.name,
        GARMENT_COUNT: String(req.garmentFiles.length),
        GARMENT_VIEWS: req.garmentViews.join(', ') || 'front',
        FORMAT: req.aspect,
        WIDTH: String(size.w),
        HEIGHT: String(size.h),
    });
    onStage('Rendering');
    const raw = await geminiImage(prompt, [crop, ...req.garmentFiles], {
        aspect: req.aspect,
        resolution: '1k',
        fit: false,
        promptAsIs: true,
        onLog: (msg: string) => { appendLog(msg); onStage(msg); },
    });
    return Object.assign({}, await studioFit(raw, req.aspect), { description: '' });
}

// One look. onStage reports what is happening (Reading the references,
// Rendering, Retrying ...) so the card can say it. The look carries the
// notes it was made from. The image model gets the photo to edit first, then
// the packshot: the first chosen reference is the person, the outfit and the
// place the look keeps; any others are only read in step 1.
// Several looks may run at once, so each reports its stage through its own
// onStage (never the shared stage hook), and the log stays Studio's while any runs.
let __studioRuns = 0;
async function generateStudioLook(req: StudioRequest, onStage: (msg: string) => void): Promise<StudioLookResult> {
    __logContext = 'studio';
    __studioRuns++;
    try {
        // Half body and close-up: from the full body look.
        if (req.shot && STUDIO_SHOT_BOX[req.shot]) return await generateStudioShot(req, onStage);
        // No prompt or no reference, no look: nothing is sent to an engine.
        if (!studioPromptTemplate() || !studioAnalysisTemplate()) throw new Error('Studio has no prompt yet');
        if (!req.referenceFiles.length) throw new Error('Choose reference photos first');
        onStage('Preparing');
        await discoverModels();
        const description = await describeStudioReferences(req, onStage);
        const prompt = buildStudioPrompt(req, description);
        onStage('Rendering');
        const raw = await geminiImage(prompt, [req.referenceFiles[0], ...req.garmentFiles], {
            aspect: req.aspect,
            resolution: '1k',
            fit: false,
            promptAsIs: true,   // the prompt manager's text is the whole prompt
            onLog: (msg: string) => { appendLog(msg); onStage(msg); },
        });
        const fitted = await studioFit(raw, req.aspect);
        return Object.assign({}, fitted, { description });
    } finally {
        if (--__studioRuns <= 0) { __studioRuns = 0; __logContext = 'packshot'; }
    }
}
